class LipImageMenu extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.mobile = this.dataset.lipMode === 'mobile';
    this.groups = [...this.querySelectorAll('details[data-lip-group]')];
    this.hostDrawer = this.closest('header-drawer');
    this.drawer = this.closest('.menu-drawer');
    this.outerDetails = this.hostDrawer?.querySelector(':scope > details');
    this.autoplayPaused = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.updatePauseControl();
    this.groups.forEach(group => {
      const summary = group.querySelector(':scope > summary');
      summary.setAttribute('aria-controls', group.querySelector('.lip-panel').id);
      group.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', String(group.open));
        if (group.open) this.closeGroups(group);
        this.scheduleAutoplay();
      }, options);
      group.addEventListener('pointerenter', event => {
        if (!this.mobile && event.pointerType === 'mouse') {
          clearTimeout(this.leaveTimer);
          this.keyboardOpen = false;
          this.closeGroups(group);
          group.open = true;
        }
      }, options);
      group.addEventListener('pointerleave', event => {
        if (!this.mobile && event.pointerType === 'mouse') {
          if (this.keyboardOpen) return;
          this.leaveTimer = setTimeout(() => { group.open = false; }, 160);
        }
      }, options);
      summary.addEventListener('click', event => {
        clearTimeout(this.leaveTimer);
        this.keyboardOpen = event.detail === 0;
        if (!this.mobile) {
          if (event.detail > 0) {
            event.preventDefault();
            this.closeGroups(group);
            group.open = true;
          }
          return;
        }
        requestAnimationFrame(() => {
          if (group.open) {
            this.drawer.scrollTop = 0;
            group.querySelector('[data-lip-back]')?.focus();
          }
        });
      }, options);
    });
    this.addEventListener('click', event => {
      const target = event.target.closest('button');
      if (!target) return;
      if (target.hasAttribute('data-lip-back')) {
        const group = target.closest('details');
        group.open = false;
        group.querySelector('summary').focus();
      }
      if (target.hasAttribute('data-carousel-pause')) {
        this.autoplayPaused = !this.autoplayPaused;
        this.updatePauseControl();
        this.scheduleAutoplay();
      }
      if (target.hasAttribute('data-carousel-prev') || target.hasAttribute('data-carousel-next')) {
        const carousel = target.closest('.lip-carousel-wrap').querySelector('[data-lip-carousel]');
        const direction = target.hasAttribute('data-carousel-next') ? 1 : -1;
        this.advanceCarousel(carousel, direction);
        this.scheduleAutoplay();
      }
    }, options);
    this.addEventListener('keydown', event => {
      const group = event.target.closest('details[data-lip-group]');
      const summary = group?.querySelector('summary');
      if (event.key === 'Escape' && group?.open) {
        event.preventDefault();
        event.stopPropagation();
        group.open = false;
        summary.focus();
        return;
      }
      if (this.mobile && event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.hostDrawer?.close();
        return;
      }
      if (event.target === summary && ['ArrowDown','ArrowUp'].includes(event.key)) {
        event.preventDefault();
        clearTimeout(this.leaveTimer);
        this.keyboardOpen = true;
        this.closeGroups(group);
        group.open = true;
        const links = [...group.querySelectorAll('.lip-panel a[href],.lip-panel button')].filter(el => el.getClientRects().length);
        const target = event.key === 'ArrowDown' ? links[0] : links.at(-1);
        target?.focus();
        target?.scrollIntoView({ block:'nearest', inline:'nearest' });
      }
      if (!this.mobile && event.target.hasAttribute('data-root-control') && ['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) {
        event.preventDefault();
        const roots = [...this.querySelectorAll('[data-root-control]')];
        const index = roots.indexOf(event.target);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? roots.length-1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + roots.length) % roots.length;
        this.closeGroups();
        roots[next].focus();
      }
    }, options);
    this.addEventListener('focusout', event => {
      if (!this.mobile && event.relatedTarget && !this.contains(event.relatedTarget)) this.closeGroups();
    }, options);
    document.addEventListener('pointerdown', event => {
      if (!this.mobile && !this.contains(event.target)) this.closeGroups();
    }, options);
    this.addEventListener('keyup', event => {
      if (this.mobile && event.key === 'Escape') event.stopPropagation();
    }, options);
    this.outerDetails?.addEventListener('toggle', () => {
      if (!this.outerDetails.open) {
        this.closeGroups();
        this.outerDetails.querySelector(':scope > summary')?.focus();
      }
    }, options);
    this.resizeQuery = matchMedia('(min-width:768px)');
    this.resizeQuery.addEventListener('change', () => {
      this.closeGroups();
      if (this.outerDetails?.open) this.hostDrawer?.close();
    }, options);
  }
  updatePauseControl() {
    const button = this.querySelector('[data-carousel-pause]');
    if (!button) return;
    button.setAttribute('aria-pressed', String(this.autoplayPaused));
    button.setAttribute('aria-label', this.autoplayPaused ? button.dataset.playLabel : button.dataset.pauseLabel);
    button.querySelector('span').textContent = this.autoplayPaused ? '▶' : 'Ⅱ';
  }
  advanceCarousel(carousel, direction) {
    const atEnd = carousel.scrollLeft + carousel.clientWidth >= carousel.scrollWidth - 2;
    const atStart = carousel.scrollLeft <= 2;
    const gap = parseFloat(getComputedStyle(carousel).columnGap) || 0;
    const step = carousel.firstElementChild.getBoundingClientRect().width + gap;
    const left = direction > 0 && atEnd ? 0 : direction < 0 && atStart ? carousel.scrollWidth : carousel.scrollLeft + direction * step;
    carousel.scrollTo({ left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }
  scheduleAutoplay() {
    clearTimeout(this.autoplayTimer);
    if (this.autoplayPaused) return;
    const carousel = this.querySelector('details[data-lip-group][open] [data-lip-carousel]');
    if (!carousel) return;
    this.autoplayTimer = setTimeout(() => {
      if (document.visibilityState === 'visible' && carousel.getClientRects().length && !carousel.matches(':hover') && !carousel.contains(document.activeElement)) {
        this.advanceCarousel(carousel, 1);
      }
      this.scheduleAutoplay();
    }, 5000);
  }
  closeGroups(except) {
    this.groups.forEach(group => { if (group !== except) group.open = false; });
  }
  disconnectedCallback() {
    clearTimeout(this.leaveTimer);
    clearTimeout(this.autoplayTimer);
    this.controller?.abort();
    this.controller = null;
  }
}
if (!customElements.get('lip-image-menu')) customElements.define('lip-image-menu', LipImageMenu);
